// DS ui_kits/grackle-app/InviteDialog.jsx + SettingsScreen.jsx (SD35). orgs.rs: GET/PATCH/DELETE
// /orgs/{org_id}/members[/{user_id}], GET /orgs/{org_id}/invites, POST /orgs/{org_id}/invites/bulk,
// DELETE /orgs/{org_id}/invites/{invite_id}; org_policy.rs: POST /orgs/{org_id}/join-links.
import { useCallback, useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { Badge, Card, ChipInput, Dialog, IconButton, Select } from "@grackle/ui";
import ConfirmDialog from "../components/ConfirmDialog";
import { api, ApiFail } from "../api/client";
import type { BulkInviteItem, InviteOut, JoinLinkCreated, JoinableRole, ListResponse, MemberOut, OrgRole } from "../api/types";
import { useSession } from "../session";
import { Button, Notice } from "../components/ui";
import type { AdminOutletContext } from "./AdminLayout";

function roleLabel(role: OrgRole): string {
  return role === "owner" ? "Owner" : role === "admin" ? "Admin" : role === "guest" ? "Guest" : "Member";
}

// FR-O4: the three roles an invite can grant, with what each one can do (REQUIREMENTS §4.10).
const JOINABLE_ROLES: { value: JoinableRole; label: string }[] = [
  { value: "member", label: "Member — can record and share" },
  { value: "admin", label: "Admin — manages members and settings" },
  { value: "guest", label: "Guest — can watch what is shared with them" },
];

const MEMBER_ROLES: { value: OrgRole; label: string }[] = [
  { value: "owner", label: "Owner" },
  { value: "admin", label: "Admin" },
  { value: "member", label: "Member" },
  { value: "guest", label: "Guest" },
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** DS InviteDialog: emails as chips, one role for all of them, and the invite-link band. */
export function InviteDialog({ orgId, orgName, open, onClose, onInvited }: { orgId: string; orgName: string; open: boolean; onClose: () => void; onInvited: () => Promise<void> | void }) {
  const [emails, setEmails] = useState<string[]>([]);
  const [role, setRole] = useState<JoinableRole>("member");
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState<BulkInviteItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [link, setLink] = useState<JoinLinkCreated | null>(null);
  const [copied, setCopied] = useState(false);
  const [linkError, setLinkError] = useState<string | null>(null);

  const invalid = emails.filter((e) => !EMAIL_RE.test(e));
  const sent = results !== null && results.every((r) => r.ok);

  async function onSend() {
    if (!emails.length || invalid.length) return;
    setSubmitting(true);
    setError(null);
    setResults(null);
    try {
      const res = await api<{ results: BulkInviteItem[] }>("POST", `/orgs/${orgId}/invites/bulk`, { emails, role });
      setResults(res.results);
      await onInvited();
    } catch (err) {
      setError(err instanceof ApiFail ? err.message : "Couldn't send those invites.");
    } finally {
      setSubmitting(false);
    }
  }

  // The raw join-link token is returned exactly once, by the create call, so the first copy
  // mints a member link (reused for the rest of this dialog) and later copies reuse it.
  async function onCopyLink() {
    setLinkError(null);
    try {
      let current = link;
      if (!current) {
        current = await api<JoinLinkCreated>("POST", `/orgs/${orgId}/join-links`, { role: "member" });
        setLink(current);
      }
      await navigator.clipboard.writeText(current.url);
      setCopied(true);
    } catch (err) {
      setLinkError(err instanceof ApiFail ? err.message : "Couldn't copy a join link.");
    }
  }

  return (
    <Dialog
      open={open}
      title={`Invite people to ${orgName}`}
      onClose={onClose}
      width={540}
      footer={
        <Button onClick={onSend} loading={submitting} disabled={!emails.length || invalid.length > 0 || sent} icon={sent ? "check" : undefined}>
          {sent ? "Invites sent" : "Send invite"}
        </Button>
      }
      aside={
        <div className="admin-invite-aside">
          <div className="admin-invite-aside__text">
            <div className="admin-invite-aside__title">Or share an invite link</div>
            <div className="admin-invite-aside__sub">Anyone who signs up with it joins as a member. It works until you revoke it in Settings.</div>
            {linkError ? <div className="admin-invite-aside__sub">{linkError}</div> : null}
          </div>
          <Button variant="secondary" size="sm" icon={copied ? "check" : "link-simple"} onClick={onCopyLink}>
            {copied ? "Copied" : "Copy link"}
          </Button>
        </div>
      }
    >
      <div className="admin-invite-form">
        <ChipInput
          label="Invite your teammates"
          placeholder="Add emails"
          inputType="email"
          values={emails}
          onChange={(v) => {
            setEmails(v);
            setResults(null);
          }}
        />
        {invalid.length ? <div className="admin-field-error">Not an email address: {invalid.join(", ")}</div> : null}
        <Select label="They join as" value={role} onChange={(v) => setRole(v as JoinableRole)} options={JOINABLE_ROLES} />
        <div className="admin-invite-caption">They'll get an email with a link to join and start recording.</div>
        {error ? <Notice tone="error">{error}</Notice> : null}
        {results ? (
          <ul className="admin-bulk-results">
            {results.map((r) => (
              <li key={r.email}>
                <span className="gk-mono">{r.email}</span>
                <Badge size="sm" tone={r.ok ? "success" : "record"}>
                  {r.ok ? "Invited" : r.reason}
                </Badge>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </Dialog>
  );
}

export default function MembersPage() {
  const { org } = useOutletContext<AdminOutletContext>();
  const { user: me } = useSession();

  const [members, setMembers] = useState<MemberOut[]>([]);
  const [invites, setInvites] = useState<InviteOut[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rowError, setRowError] = useState<string | null>(null);
  const [removeConfirm, setRemoveConfirm] = useState<{ userId: string; name: string } | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [membersRes, invitesRes] = await Promise.all([
        api<ListResponse<MemberOut>>("GET", `/orgs/${org.id}/members`),
        api<ListResponse<InviteOut>>("GET", `/orgs/${org.id}/invites`),
      ]);
      setMembers(membersRes.items);
      setInvites(invitesRes.items);
    } catch (err) {
      setError(err instanceof ApiFail ? err.message : "Couldn't load members.");
    } finally {
      setLoading(false);
    }
  }, [org.id]);

  useEffect(() => {
    void load();
  }, [load]);

  async function onRoleChange(userId: string, role: OrgRole) {
    setRowError(null);
    try {
      await api("PATCH", `/orgs/${org.id}/members/${userId}`, { role });
      await load();
    } catch (err) {
      setRowError(err instanceof ApiFail ? err.message : "Couldn't change that member's role.");
    }
  }

  async function onRemoveConfirm() {
    if (!removeConfirm) return;
    const { userId } = removeConfirm;
    setRemoveConfirm(null);
    setRowError(null);
    try {
      await api("DELETE", `/orgs/${org.id}/members/${userId}`);
      await load();
    } catch (err) {
      setRowError(err instanceof ApiFail ? err.message : "Couldn't remove that member.");
    }
  }

  async function onRemove(userId: string, name: string) {
    setRemoveConfirm({ userId, name });
  }

  async function onRevokeInvite(inviteId: string) {
    setRowError(null);
    try {
      await api("DELETE", `/orgs/${org.id}/invites/${inviteId}`);
      await load();
    } catch (err) {
      setRowError(err instanceof ApiFail ? err.message : "Couldn't revoke that invite.");
    }
  }

  return (
    <>
      <header className="page-head">
        <div className="page-head__grow">
          <h1>Members</h1>
          <p>
            Who has access to {org.name}, and what they can do. Transcription grants live on{" "}
            <Link to={`/o/${org.slug}/admin/ai`}>AI and models</Link>.
          </p>
        </div>
        <Button variant="secondary" icon="user-plus" onClick={() => setInviteOpen(true)}>
          Invite people
        </Button>
      </header>

      {inviteOpen ? (
        <InviteDialog orgId={org.id} orgName={org.name} open onClose={() => setInviteOpen(false)} onInvited={load} />
      ) : null}

      {error ? <Notice tone="error">{error}</Notice> : null}
      {rowError ? <Notice tone="error">{rowError}</Notice> : null}

      {loading ? (
        <div className="admin-row-sub">Loading…</div>
      ) : (
        <Card size="flush" className="admin-table-card">
          <table className="admin-table">
            <thead>
              <tr>
                <th className="gk-overline">Name</th>
                <th className="gk-overline">Role</th>
                <th className="gk-overline">Transcribe</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {members.map((m) => (
                <tr key={m.user.id}>
                  <td>
                    <div className="admin-row-name">{m.user.name || m.user.email}</div>
                    <div className="admin-row-sub">{m.user.email}</div>
                  </td>
                  <td>
                    <Select
                      size="sm"
                      aria-label={`Role for ${m.user.name || m.user.email}`}
                      value={m.role}
                      disabled={m.user.id === me?.id}
                      onChange={(v) => void onRoleChange(m.user.id, v as OrgRole)}
                      options={MEMBER_ROLES}
                    />
                  </td>
                  <td>
                    <span className="admin-row-sub">{m.can_transcribe ? "Yes" : "No"}</span>
                  </td>
                  <td>
                    <div className="row-actions">
                      {m.user.id !== me?.id ? (
                        <IconButton icon="trash" label={`Remove ${m.user.name || m.user.email}`} size="sm" variant="muted" onClick={() => onRemove(m.user.id, m.user.name || m.user.email)} />
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))}
              {invites.map((inv) => (
                <tr key={inv.id}>
                  <td>
                    <div className="admin-row-name">{inv.email}</div>
                    <div className="admin-row-sub">Invited · pending</div>
                  </td>
                  <td>
                    <Badge size="sm">{roleLabel(inv.role)}</Badge>
                  </td>
                  <td />
                  <td>
                    <div className="row-actions">
                      <IconButton icon="trash" label={`Revoke invite to ${inv.email}`} size="sm" variant="muted" onClick={() => onRevokeInvite(inv.id)} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {/* Issue #1: replace window.confirm */}
      {removeConfirm && (
        <ConfirmDialog
          open={true}
          title="Remove member"
          message={`Remove ${removeConfirm.name} from ${org.name}? Any recordings they own will transfer to you.`}
          confirmLabel="Remove"
          danger
          onClose={() => setRemoveConfirm(null)}
          onConfirm={onRemoveConfirm}
        />
      )}
    </>
  );
}
